import axios, { AxiosError, AxiosResponse } from 'axios'

/**
 * Function returning Promise that resolves after specified time and can be awaited as a delay in async fnc
 * @param ms - How many milliseconds will take the Promise to get resolved
 * @returns Promise
 */
export const waitFor = (ms: number) => {
  return new Promise((resolve) => {
    setTimeout(resolve, ms)
  })
}

/**
 * A failure worth another attempt: no response at all (network drop, DNS, timeout), a 5xx, or the
 * two 4xx the server itself asks you to come back on. Every other 4xx is a verdict, not a blip, and
 * a cancellation is the user's decision — retrying either just burns requests.
 */
export const isRetryableRequestFailure = (error: AxiosError) => {
  if (axios.isCancel(error)) return false

  const status = error.response?.status

  if (!status) return true

  return status >= 500 || status === 408 || status === 429
}

/**
 * Function that will call request until returns success or until retryLimit is reached with specified delay in between calls
 * @param fnc - Function which should perform axios call and return Promise with AxiosResponse
 * @param retryDelay - How long delay should be between calls in milliseconds (default 500)
 * @param retryLimit - How many attempts are allowed in total (default 30)
 * @param shouldRetry - Decides whether a given failure is worth retrying (default: retry everything)
 * @returns Either AxiosResponse or AxiosError
 */
export async function retryRequestUntilSuccess<T = unknown>(fnc: () => Promise<AxiosResponse<T>>, retryDelay = 500, retryLimit = 30, shouldRetry: (error: AxiosError) => boolean = () => true) {

  let retryCount = 0
  let lastError: AxiosError

  do {
    try {
      const result = await fnc()
      return result
    } catch (err: unknown) {
      lastError = err as AxiosError

      if (!shouldRetry(lastError)) return lastError
    }

    await waitFor(retryDelay)

    retryCount++
  } while (retryCount < retryLimit)

  return lastError
}
